import {
	analyzerLinks, cellPopover, chartFullscreen, closeCellPopover, closeEvolutionModal, closePopoverIfLast, columnHeader,
	carPickField, detailsToggle, evolutionModal, featuresSelector, minMaxToggle, missingInPopover, openCellPopover, openColumnEvolution,
	popoverButton, priceCell, priceCellBox, pricesChart, priceTable, reportSelector,
} from 'Pages/AnalyzersV2/@tour/targets'
import { demoPopoverOpen } from './signals'
import { hasRepricing, LINKS_PASS_TEXT, SELECTOR_PASS_TEXT } from 'Pages/AnalyzersV2/@tour/repricing'

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	// Intro
	{
		type: 'intro',
		icon: 'tally',
		title: ES ? 'Bienvenido al Analizador LOR' : 'Welcome to the LOR Analyzer',
		description: ES
			? 'El Analizador de Duración de Alquiler te permite comparar precios en distintas duraciones de alquiler. Observa cómo los competidores fijan precios para alquileres de 1 día frente a 7 días en la misma oficina y fechas.'
			: 'The Length of Rental Analyzer lets you compare prices across different rental durations. See how competitors price 1-day vs 7-day rentals for the same office and dates.',
	},
	// Report Selector
	{
		type: 'field',
		skipIf: () => !reportSelector(),
		findTarget: reportSelector,
		icon: 'file-lines',
		title: ES ? 'Selector de Informe' : 'Report Selector',
		get description() {
			return (ES
			? 'Selecciona una ejecución de informe de este desplegable para cargar los datos del análisis. Los informes contienen comparativas de precios entre distintas duraciones de alquiler y proveedores.'
			: 'Select a report execution from this dropdown to load the analysis data. Reports contain pricing comparisons across different rental durations and providers.') + (hasRepricing() ? SELECTOR_PASS_TEXT[ES ? 'es' : 'en'] : '')
		},
	},
	// Analyzer shortcuts (only with a report selected)
	{
		type: 'field',
		skipIf: () => !analyzerLinks(),
		findTarget: analyzerLinks,
		icon: 'arrow-up-right-from-square',
		title: ES ? 'Otros Analizadores' : 'Other Analyzers',
		get description() {
			return (ES
			? 'Estos botones abren el mismo informe en otro analizador (DOR, Sitio Directo, Brokers o Rating) en una pestaña nueva. Solo aparecen los analizadores disponibles para este informe.'
			: 'These buttons open the same report in another analyzer (DOR, Direct Site, Brokers or Rating) in a new tab. Only the analyzers available for this report appear.') + (hasRepricing() ? LINKS_PASS_TEXT[ES ? 'es' : 'en'] : '')
		},
	},
	// Price Table
	{
		type: 'field',
		findTarget: priceTable,
		icon: 'table-cells',
		title: ES ? 'Tabla de Precios' : 'Price Table',
		description: ES
			? 'La tabla comparativa principal. Cada columna es una duración de alquiler (LOR) y cada fila es un sitio web/proveedor. Las celdas con código de color destacan las opciones más baratas (verde) y más caras (rojo); la leyenda bajo la tabla lo recuerda.'
			: 'The main comparison table. Each column is a rental duration (LOR) and each row is a website/provider. Color-coded cells highlight the cheapest (green) and most expensive (red) options; the legend under the table recalls it.',
		example: ES ? 'Verde = Más barato | Rojo = Más caro' : 'Green = Cheapest | Red = Most Expensive',
	},
	// Toggle Details
	{
		type: 'field',
		findTarget: detailsToggle,
		icon: 'toggle-on',
		title: ES ? 'Mostrar/Ocultar Detalles' : 'Toggle Details',
		description: ES
			? 'Con «Show details» cada celda muestra, además del precio, su variación y el precio anterior, y las características elegidas en «Features». Con «Hide details» solo queda el precio y la tabla es más compacta.'
			: 'With "Show details" each cell shows, besides the price, its change and the previous price, plus the features picked in "Features". With "Hide details" only the price is left and the table is more compact.',
	},
	// Features Selector
	{
		type: 'field',
		findTarget: featuresSelector,
		icon: 'filter',
		title: ES ? 'Selector de Características' : 'Features Selector',
		description: ES
			? 'Elige qué características se muestran dentro de cada celda — Provider, Car Model, Category, Acriss, Price, Transmission, Fuel Policy, Payment y más. Disponible mientras los detalles están visibles.'
			: 'Choose which features each cell shows — Provider, Car Model, Category, Acriss, Price, Transmission, Fuel Policy, Payment, and more. Available while details are shown.',
		example: ES ? 'Selecciona: Provider, Category, Price, Acriss' : 'Select: Provider, Category, Price, Acriss',
	},
	// Cars (CU-869fmpqfm)
	{
		type: 'field',
		findTarget: carPickField,
		icon: 'car',
		title: ES ? 'Coches' : 'Cars',
		description: ES
			? 'Cada celda es el precio más bajo (o más alto) de todos los coches que cumplen tus filtros, así que puede comparar coches distintos. Elige aquí uno o varios Acriss, categorías o grupos Acriss (un tipo cada vez) y la tabla, el gráfico y el Excel pasan a ser solo de esos coches, sin pulsar Search. "All cars" vuelve a la tabla de todos.'
			: 'Each cell is the lowest (or highest) price of every car that matches your filters, so it can compare different cars. Pick one or several Acriss codes, categories or Acriss groups here (one kind at a time) and the table, the chart and the Excel show only those cars, without pressing Search. "All cars" goes back to every car.',
	},
	// Min / Max View
	{
		type: 'field',
		findTarget: minMaxToggle,
		icon: 'arrow-down-arrow-up',
		title: ES ? 'Vista Mín / Máx' : 'Min / Max View',
		description: ES
			? 'Alterna entre la vista de precio mínimo (opción más barata por sitio) y precio máximo (la más cara). Útil para entender el rango completo de precios que ofrece cada proveedor.'
			: 'Switch between the minimum price (cheapest option per site) and maximum price (most expensive) view. Useful for understanding the full price range each provider offers.',
	},
	// Price Cell — Next opens its popover (demo signal or a click on the real cell)
	{
		type: 'field',
		findTarget: priceCellBox,
		action: () => openCellPopover(demoPopoverOpen),
		waitFor: () => !!cellPopover() || !priceCell(),
		cleanup: () => closeCellPopover(demoPopoverOpen),
		icon: 'hand-pointer',
		title: ES ? 'Celda de Precio' : 'Price Cell',
		description: ES
			? 'Haz clic en cualquier celda de precio para abrir un panel con el detalle de esa oferta: nombre del vehículo, características y accesos directos a herramientas de análisis.'
			: 'Click on any price cell to open a detail panel for that offer: vehicle name, features, and shortcuts to analysis tools.',
	},
	// Popover overview
	{
		type: 'field',
		findTarget: cellPopover,
		icon: 'car',
		title: ES ? 'Detalle del Vehículo' : 'Vehicle Details',
		description: ES
			? 'El panel muestra el nombre del vehículo y sus características: categoría, política de combustible, tipo de pago y fecha de recogida. Datos exactos capturados por el rastreador en esa ejecución.'
			: 'The panel shows the vehicle name and its features: category, fuel policy, payment type, and pickup date. Exact data captured by the scraper during that execution.',
	},
	// Evolution button
	{
		type: 'field',
		findTarget: () => popoverButton('tour-popover-evolution'),
		action: closePopoverIfLast(demoPopoverOpen, 'tour-popover-realtime', 'tour-popover-screenshot'),
		icon: 'chart-line',
		title: ES ? 'Botón Evolución' : 'Evolution Button',
		description: ES
			? 'Abre el gráfico de evolución histórica de precios para esa celda concreta. Muestra cómo ha variado el precio de ese vehículo en ese proveedor a lo largo del tiempo.'
			: 'Opens the historical price evolution chart for that specific cell. Shows how the price for that vehicle at that provider has changed over time.',
	},
	// Real time button
	{
		type: 'field',
		skipIf: () => missingInPopover('tour-popover-realtime'),
		findTarget: () => popoverButton('tour-popover-realtime'),
		action: closePopoverIfLast(demoPopoverOpen, 'tour-popover-screenshot'),
		icon: 'magnifying-glass',
		title: ES ? 'Botón Búsqueda en Tiempo Real' : 'Real-Time Search Button',
		description: ES
			? 'Abre la búsqueda en Real-Time con todos los filtros de esta celda precargados. Solo disponible si el rastreador encontró datos de búsqueda correspondientes.'
			: 'Opens the Real-Time search with all filters for this cell pre-loaded. Only available if the scraper found corresponding search data.',
	},
	// Screenshot button — Next closes the popover
	{
		type: 'field',
		skipIf: () => missingInPopover('tour-popover-screenshot'),
		findTarget: () => popoverButton('tour-popover-screenshot'),
		action: () => closeCellPopover(demoPopoverOpen),
		icon: 'image',
		title: ES ? 'Botón Captura de Pantalla' : 'Screenshot Button',
		description: ES
			? 'Muestra la captura de pantalla tomada por el rastreador en el momento de la extracción. Solo disponible si el rastreador guardó una imagen para esta oferta.'
			: 'Shows the screenshot captured by the scraper at the time of extraction. Only available if the scraper saved an image for this offer.',
	},
	// Column Header — Next opens the column's evolution chart; Back re-opens the cell popover
	{
		type: 'field',
		findTarget: columnHeader,
		action: openColumnEvolution,
		waitFor: () => !!evolutionModal(),
		reverseAction: () => openCellPopover(demoPopoverOpen),
		icon: 'table-columns',
		title: ES ? 'Cabecera de Columna' : 'Column Header',
		description: ES
			? 'Haz clic en el encabezado de cualquier columna LOR para abrir el gráfico de evolución de todos los proveedores en esa duración de alquiler. Útil para comparar tendencias de un LOR completo sin tener que seleccionar celda por celda.'
			: 'Click on any LOR column header to open the evolution chart for all providers at that rental duration. Useful for comparing trends across an entire LOR without selecting cell by cell.',
	},
	// Evolution Chart (modal open) — Next closes it
	{
		id: 'evolution-chart',
		type: 'field',
		findTarget: evolutionModal,
		action: closeEvolutionModal,
		cleanup: closeEvolutionModal,
		icon: 'chart-line',
		title: ES ? 'Gráfico de Evolución' : 'Evolution Chart',
		description: ES
			? 'Vista completa de la evolución de precios para todos los proveedores en la duración de alquiler seleccionada. Muestra tendencias a lo largo del tiempo con líneas por proveedor, permitiendo identificar patrones de pricing competitivo.'
			: 'Full view of price evolution for all providers at the selected rental duration. Shows trends over time with lines per provider, letting you identify competitive pricing patterns.',
	},
	// Price Chart
	{
		type: 'field',
		findTarget: pricesChart,
		icon: 'chart-line',
		title: ES ? 'Gráfico de Precios' : 'Price Chart',
		description: ES
			? 'Comparación visual de precios en distintas duraciones de alquiler. Alterna entre «Price values» (importes) y «Variation (%)» (variación porcentual) para detectar patrones de precios.'
			: 'Visual comparison of prices across rental durations. Toggle between "Price values" (amounts) and "Variation (%)" (percentage change) to spot pricing patterns.',
	},
	// Fullscreen Chart
	{
		type: 'field',
		findTarget: chartFullscreen,
		icon: 'expand',
		title: ES ? 'Gráfico en Pantalla Completa' : 'Fullscreen Chart',
		description: ES
			? 'Amplía el gráfico a pantalla completa para una vista más clara. Especialmente útil al comparar muchos proveedores o duraciones de alquiler.'
			: 'Expand the chart to full screen for a clearer view. Especially useful when comparing many providers or rental durations.',
	},
	// Outro
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Usa los botones de navegación de arriba para cambiar entre los analizadores LOR, DOR, Brokers, Sitio Directo y Rating.'
			: 'Use the analyzer navigation buttons above to switch between LOR, DOR, Brokers, Direct Site, and Rating analyzers.',
	},
]
